import { useState, type ReactNode } from 'react';
import {
  Badge,
  Box,
  Card,
  Group,
  LoadingOverlay,
  Pagination,
  Popover,
  ScrollArea,
  Stack,
  Table,
  Text,
  UnstyledButton,
} from '@mantine/core';

import { CustomCopyBtn, formatAmount, useResponsive } from '@/shared';
import { dayjsCustom } from '@/shared/utils/helpers/datetime-format';

import {
  formatAge,
  h2hDiscrepancyReasonTitles,
  getH2HPaymentSystemTitle,
  h2hMerchantCallbackStatusTitles,
  h2hProviderStatusTitles,
  h2hWorkflowStatusColors,
  h2hWorkflowStatusTitles,
} from '../utils/presentation';
import { H2HPaymentActions } from './h2h-payment-actions';

import type { TH2HPayment, TH2HSettlementSource } from '../store/types';

type PaymentActionHandlers = {
  onCredit: (paymentId: number, paidAmount: string) => void;
  onFinalize: (paymentId: number, settlementSource: TH2HSettlementSource) => void;
  onReject: (paymentId: number, reason: string) => void;
  onResolveProviderConflict: (paymentId: number) => void;
  onReviewDiscrepancy: (paymentId: number) => void;
  onHideFromQueue: (paymentId: number) => void;
};

type ActionProps = PaymentActionHandlers & {
  isLoading: boolean;
  showHideFromQueue: boolean;
};

const formatProviderId = (providerId: string) => {
  if (providerId.length <= 18) return providerId;

  return `${providerId.slice(0, 9)}…${providerId.slice(-6)}`;
};

const ProviderId = ({
  providerId,
  align = 'flex-end',
}: {
  providerId: string | null;
  align?: 'flex-start' | 'flex-end';
}) => {
  if (!providerId) return <Text size="xs">-</Text>;

  return (
    <Group gap={2} justify={align} wrap="nowrap">
      <CustomCopyBtn
        text={providerId}
        showText={false}
        leftIcon
        tooltipPosition="top"
      />
      <Text
        size="xs"
        title={providerId}
        ta={align === 'flex-end' ? 'right' : 'left'}
        style={{ whiteSpace: 'nowrap' }}
      >
        {formatProviderId(providerId)}
      </Text>
    </Group>
  );
};

const Credentials = ({
  credentials,
  methodName,
  align = 'flex-end',
}: {
  credentials: string | null;
  methodName: string;
  align?: 'flex-start' | 'flex-end';
}) => {
  const [opened, setOpened] = useState(false);
  const values = credentials?.split('/').map(value => value.trim()).filter(Boolean) ?? [];
  const primaryCredential = values[0];

  if (!primaryCredential) return <Text size="xs">-</Text>;

  const textAlign = align === 'flex-end' ? 'right' : 'left';

  return (
    <Group gap={2} justify={align} wrap="nowrap">
      <CustomCopyBtn
        text={primaryCredential}
        showText={false}
        leftIcon
        tooltipPosition="top"
      />
      <Popover
        opened={opened}
        onChange={setOpened}
        position={align === 'flex-end' ? 'bottom-end' : 'bottom-start'}
        shadow="md"
        width={240}
        withArrow
        withinPortal
      >
        <Popover.Target>
          <UnstyledButton onClick={() => setOpened(current => !current)}>
            <Text
              size="xs"
              fw={500}
              ta={textAlign}
              style={{ borderBottom: '1px dashed currentColor', wordBreak: 'break-all' }}
            >
              {primaryCredential}
            </Text>
          </UnstyledButton>
        </Popover.Target>
        <Popover.Dropdown>
          <Stack gap={4}>
            <Text size="xs" fw={600} ta={textAlign}>{methodName}</Text>
            {values.slice(1).map((value, index) => (
              <Text size="xs" ta={textAlign} key={`${value}-${index}`}>{value}</Text>
            ))}
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </Group>
  );
};

const PaymentAmounts = ({ payment }: { payment: TH2HPayment }) => (
  <Text size="xs" style={{ whiteSpace: 'nowrap' }}>
    <span style={{ fontWeight: 500 }}>
      {formatAmount(payment.baseAmount)} {payment.baseCurrency}
    </span>
    {payment.providerAmount && payment.providerCurrency
      ? ` (${formatAmount(payment.providerAmount)} ${payment.providerCurrency})`
      : ''}
  </Text>
);

const CallbackStatus = ({
  payment,
  align = 'flex-start',
}: {
  payment: TH2HPayment;
  align?: 'flex-start' | 'flex-end';
}) => {
  const status = payment.providerCallbackStatus
    ? h2hProviderStatusTitles[payment.providerCallbackStatus] ?? payment.providerCallbackStatus
    : 'Не получен';
  const receivedAt = payment.providerCallbackReceivedAt
    ? ` ${dayjsCustom(payment.providerCallbackReceivedAt).format('DD.MM [в] HH:mm')}`
    : '';

  return (
    <Text
      size="xs"
      fw={500}
      ta={align === 'flex-end' ? 'right' : 'left'}
      style={{ whiteSpace: 'nowrap' }}
    >
      {status}{receivedAt}
    </Text>
  );
};

const getShopSettlementStatus = (payment: TH2HPayment) => payment.settlementStatus === 'approved'
  ? `Зачислен ${formatAmount(payment.settlementAmount ?? '0')} ${payment.settlementCurrency ?? ''}`
  : 'Не зачислен';

const getPartnerSettlementStatus = (payment: TH2HPayment) => {
  if (payment.partnerShopId === null) return 'Нет партнёра';

  return payment.partnerCommissionCredited
    ? `Зачислен ${formatAmount(payment.partnerCommission)} ${payment.settlementCurrency ?? ''}`
    : 'Не зачислен';
};

const isReviewedDiscrepancy = (payment: TH2HPayment) => (
  payment.workflowStatus === 'discrepancy'
  && payment.discrepancyReviewedAt !== null
);

const getWorkflowStatusTitle = (payment: TH2HPayment) => (
  isReviewedDiscrepancy(payment)
    ? 'Расхождение проверено'
    : h2hWorkflowStatusTitles[payment.workflowStatus]
);

const getWorkflowStatusColor = (payment: TH2HPayment) => (
  isReviewedDiscrepancy(payment)
    ? 'gray'
    : h2hWorkflowStatusColors[payment.workflowStatus]
);

const SettlementStatus = ({ payment }: { payment: TH2HPayment }) => (
  <Stack gap={2}>
    <Text size="xs">Магазин: {getShopSettlementStatus(payment)}</Text>
    <Text size="xs">Партнёр: {getPartnerSettlementStatus(payment)}</Text>
  </Stack>
);

const WorkflowStatus = ({ payment }: { payment: TH2HPayment }) => (
  <Stack gap={4} align="flex-start">
    <Badge color={getWorkflowStatusColor(payment)} variant="light" size="sm">
      {getWorkflowStatusTitle(payment)}
    </Badge>
    {payment.discrepancyReason && (
      <Text size="xs" c={isReviewedDiscrepancy(payment) ? 'dimmed' : 'red'}>
        {h2hDiscrepancyReasonTitles[payment.discrepancyReason]}
      </Text>
    )}
    <Text size="xs">
      Клиент: {payment.clientCreditedAt ? 'зачислено' : 'не зачислено'}
    </Text>
    <Text size="xs">
      Коллбэк магазину: {h2hMerchantCallbackStatusTitles[payment.merchantCallbackStatus]}
    </Text>
  </Stack>
);

const MobileField = ({ label, children }: { label: string; children: ReactNode }) => (
  <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
    <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>{label}</Text>
    <Box style={{ minWidth: 0, textAlign: 'right' }}>{children}</Box>
  </Group>
);

const DesktopRow = ({ payment, actions }: { payment: TH2HPayment; actions: ActionProps }) => {
  return (
    <Table.Tr>
      <Table.Td>
        <Stack gap={3}>
          <CustomCopyBtn text={payment.paymentId.toString()} rightIcon />
          <Group gap={4} wrap="nowrap">
            <Text size="xs" c="dimmed">
              {payment.paymentSystemName
                ? getH2HPaymentSystemTitle(payment.paymentSystemName)
                : 'Провайдер'}
            </Text>
            <ProviderId providerId={payment.remoteId} align="flex-start" />
          </Group>
        </Stack>
      </Table.Td>
      <Table.Td><PaymentAmounts payment={payment} /></Table.Td>
      <Table.Td>
        <Credentials
          credentials={payment.credentials}
          methodName={payment.methodName}
          align="flex-start"
        />
      </Table.Td>
      <Table.Td>
        <Text size="xs">{payment.shopId} — {payment.shopName} ({payment.payerId})</Text>
      </Table.Td>
      <Table.Td>
        <Badge color={payment.slaBreached ? 'red' : 'blue'} variant="light">
          {formatAge(payment.ageSeconds)}
        </Badge>
        <Text size="xs" c="dimmed" mt={4}>
          {dayjsCustom(payment.created).format('DD.MM.YYYY / HH:mm')}
        </Text>
      </Table.Td>
      <Table.Td><WorkflowStatus payment={payment} /></Table.Td>
      <Table.Td><CallbackStatus payment={payment} /></Table.Td>
      <Table.Td><SettlementStatus payment={payment} /></Table.Td>
      <Table.Td>
        <H2HPaymentActions
          payment={payment}
          {...actions}
        />
      </Table.Td>
    </Table.Tr>
  );
};

const MobileCard = ({ payment, actions }: { payment: TH2HPayment; actions: ActionProps }) => {
  return (
    <Card withBorder padding="sm">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={2}>
            <CustomCopyBtn text={payment.paymentId.toString()} rightIcon />
            <Text size="xs" c="dimmed">{dayjsCustom(payment.created).format('DD.MM.YYYY / HH:mm')}</Text>
          </Stack>
          <Stack gap={4} align="flex-end">
            <Badge color={getWorkflowStatusColor(payment)} variant="light" size="sm">
              {getWorkflowStatusTitle(payment)}
            </Badge>
            {payment.discrepancyReason && (
              <Text
                size="xs"
                c={isReviewedDiscrepancy(payment) ? 'dimmed' : 'red'}
                ta="right"
              >
                {h2hDiscrepancyReasonTitles[payment.discrepancyReason]}
              </Text>
            )}
            <Badge color={payment.slaBreached ? 'red' : 'blue'}>{formatAge(payment.ageSeconds)}</Badge>
          </Stack>
        </Group>
        <Stack gap={6}>
          <MobileField label="Сумма заявки">
            <PaymentAmounts payment={payment} />
          </MobileField>
          <MobileField label="Магазин">
            <Text size="xs">{payment.shopId} — {payment.shopName} ({payment.payerId})</Text>
          </MobileField>
          <MobileField label={`ID ${getH2HPaymentSystemTitle(payment.paymentSystemName)}`}>
            <ProviderId providerId={payment.remoteId} />
          </MobileField>
          <MobileField label="Реквизиты">
            <Credentials
              credentials={payment.credentials}
              methodName={payment.methodName}
            />
          </MobileField>
          <MobileField
            label={`${getH2HPaymentSystemTitle(payment.paymentSystemName)} callback${payment.providerCallbackStatus ? ' (API)' : ''}`}
          >
            <CallbackStatus payment={payment} align="flex-end" />
          </MobileField>
        </Stack>
        <Stack gap={6}>
          <Text size="xs" c="dimmed" fw={600}>Статус в нашей системе</Text>
          <MobileField label="Конечный клиент">
            <Text size="xs">{payment.clientCreditedAt ? 'Зачислен' : 'Не зачислен'}</Text>
          </MobileField>
          <MobileField label="Коллбэк магазину">
            <Text size="xs">{h2hMerchantCallbackStatusTitles[payment.merchantCallbackStatus]}</Text>
          </MobileField>
          <MobileField label="Магазин">
            <Text size="xs">{getShopSettlementStatus(payment)}</Text>
          </MobileField>
          <MobileField label="Партнёр">
            <Text size="xs">{getPartnerSettlementStatus(payment)}</Text>
          </MobileField>
        </Stack>
        <H2HPaymentActions
          payment={payment}
          {...actions}
        />
      </Stack>
    </Card>
  );
};

type Props = PaymentActionHandlers & {
  items: TH2HPayment[];
  isLoading: boolean;
  isActionLoading: boolean;
  itemsCount: number;
  pageSize: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  showHideFromQueue: boolean;
};

export const H2HPaymentsTable = ({
  items,
  isLoading,
  isActionLoading,
  itemsCount,
  pageSize,
  currentPage,
  onPageChange,
  showHideFromQueue,
  ...actions
}: Props) => {
  const { isDesktop } = useResponsive();
  const totalPages = Math.ceil(itemsCount / pageSize);

  return (
    <Box pos="relative" mih={120}>
      <LoadingOverlay visible={isLoading} overlayProps={{ blur: 2 }} />
      {isDesktop ? (
        <ScrollArea type="auto" offsetScrollbars>
          <Table striped highlightOnHover miw={1350}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Payment / provider ID</Table.Th>
                <Table.Th>Сумма заявки</Table.Th>
                <Table.Th>Реквизиты</Table.Th>
                <Table.Th>Магазин / клиент</Table.Th>
                <Table.Th>Возраст / SLA</Table.Th>
                <Table.Th>Клиент / коллбэк магазину</Table.Th>
                <Table.Th>Callback провайдера (API)</Table.Th>
                <Table.Th>Начисление</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map(payment => (
                <DesktopRow
                  key={payment.paymentId}
                  payment={payment}
                  actions={{
                    ...actions,
                    isLoading: isActionLoading,
                    showHideFromQueue,
                  }}
                />
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      ) : (
        <Stack gap="sm">
          {items.map(payment => (
            <MobileCard
              key={payment.paymentId}
              payment={payment}
              actions={{
                ...actions,
                isLoading: isActionLoading,
                showHideFromQueue,
              }}
            />
          ))}
        </Stack>
      )}
      {!isLoading && items.length === 0 && <Text ta="center" py="xl">Нет данных</Text>}
      {items.length > 0 && totalPages > 1 && (
        <Group justify="center" my="md">
          <Pagination value={currentPage} onChange={onPageChange} total={totalPages} />
        </Group>
      )}
    </Box>
  );
};
